import { NavLink } from "@/layouts/AppLayout/types";
import {
  DOMAIN,
  PERMISSION,
  useCanViewBeneficiariesBool,
  useCanViewInteractionsBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { Path } from "@/router";

export const usePersonNavLinks = (pgaId: string): Record<string, NavLink<Path>[]> => {
  const canViewInteractions = useCanViewInteractionsBool();
  const canViewBeneficiaries = useCanViewBeneficiariesBool();
  const canViewPersonMembership = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonMembership
  });
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const canViewCommittees = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCommittees
  });
  const showMembershipNav = canViewPersonMembership || canManageApplications || canViewCommittees;
  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });
  const canViewOrders = useHasCapabilityBool({
    domain: DOMAIN.COMMERCE,
    permission: PERMISSION.viewOrders
  });
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });
  const showBillingNav = canViewOrders || canViewPersonBilling;

  const personLinks = [
    {
      domain: DOMAIN.EVERYONE,
      label: "Person",
      link: {
        to: "/people/:pgaId/person",
        params: { pgaId }
      },
      icon: "assignment_ind"
    },
    showMembershipNav && {
      domain: DOMAIN.EVERYONE,
      label: "Membership",
      link: {
        to: "/people/:pgaId/membership",
        params: { pgaId }
      },
      icon: "badge"
    },
    canViewPersonEducation && {
      domain: DOMAIN.EVERYONE,
      label: "Education",
      link: {
        to: "/people/:pgaId/education",
        params: { pgaId }
      },
      icon: "school"
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Career",
      link: {
        to: "/people/:pgaId/career",
        params: { pgaId }
      },
      icon: "business_center"
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Events",
      link: {
        to: "/people/:pgaId/events",
        params: { pgaId }
      },
      icon: "today"
    },
    showBillingNav && {
      domain: DOMAIN.EVERYONE,
      label: "Billing",
      link: {
        to: "/people/:pgaId/billing",
        params: { pgaId }
      },
      icon: "card_membership"
    },
    canViewBeneficiaries && {
      domain: DOMAIN.EVERYONE,
      label: "Benefits",
      link: {
        to: "/people/:pgaId/benefits",
        params: { pgaId }
      },
      icon: "volunteer_activism"
    },
    canViewInteractions && {
      domain: DOMAIN.EVERYONE,
      label: "Interactions",
      link: {
        to: "/people/:pgaId/interactions",
        params: { pgaId }
      },
      icon: "3p"
    },
    {
      domain: DOMAIN.EVERYONE,
      label: "Admin Tools",
      link: {
        to: "/people/:pgaId/admin-tools",
        params: { pgaId }
      },
      icon: "build"
    }
  ].filter(Boolean) as NavLink<Path>[];

  return {
    "Manage Person": personLinks
  };
};

// Re-export from features directory for backward compatibility
export {
  usePersonActions,
  getPersonActionButtons,
  type PersonActionButton
} from "@/features/person/hooks/usePersonActions";
